import {useThree} from "@react-three/fiber";
import {useEffect} from "react";
import * as THREE from "three";
import {resolveMaxViewDistance} from "../../../../Terrain/Heights/TerrainViewInfo.js";
import {observer_mgl_r3} from "../../../../Utils/ThreeJS/General.js";

/** Set15 P1 — sky + fog for the 3D camera. Plan §3.9: a horizon with no sky reads as broken, and
 *  exponential fog makes the far field's resolution not matter.  Terrain (custom ShaderMaterial)
 *  doesn't consume `scene.fog` until the §3.9 `<fog_*>` includes are assembled (P3/P4), but the
 *  scenery + any scene-backed meshes do — and `scene.background`/`scene.fog` are set here so the
 *  shell is coherent.
 *
 *  This is a render-body component (not a useFrame) so it re-applies the scene state when the
 *  settings change; it also re-applies on the Canvas remount that 3D mode triggers. */
export const Map3D_SkyFog = observer_mgl_r3((props: {maxViewDistanceInput: number|n, fogDensityInput: number|n})=>{
	const {maxViewDistanceInput, fogDensityInput} = props;
	const {scene} = useThree();

	const maxViewDistance = resolveMaxViewDistance(maxViewDistanceInput);
	// Derive a fog density that reaches full fog near the far plane (~e^(-density*maxViewDistance)≈0).
	// Tuned so the horizon fades into the sky colour at ~maxViewDistance.
	const fogDensity = (typeof fogDensityInput == "number" && Number.isFinite(fogDensityInput) && fogDensityInput > 0)
		? fogDensityInput
		: 3 / Math.max(1e-3, maxViewDistance);

	useEffect(()=>{
		// Sky — a gentle gradient reads as "daytime sky" without an explicit dome. Three's fog
		// Colour is matched to the horizon so far terrain fades into it.
		const skyColor = new THREE.Color("#87b7e8");
		const prevBackground = scene.background;
		const prevFog = scene.fog;
		scene.background = skyColor;
		scene.fog = new THREE.FogExp2(skyColor, fogDensity);
		return ()=>{
			// Restore on unmount so 2D (remount) isn't left with 3D scene state.
			scene.background = prevBackground;
			scene.fog = prevFog;
		};
	}, [scene, fogDensity]);

	return null;
});
